import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE } from "./Reveal";

const HERO_IMG = "/photos/hero.jpg";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.2 + i * 0.14, duration: 0.9, ease: EASE },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { delay: d, duration: 0.8, ease: EASE } }),
};

const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <div className="absolute inset-0 overflow-hidden">
        <motion.img
          src={HERO_IMG}
          alt="Member training at the Me 1st Fitness & Coaching studio in Red Deer"
          style={{ y: imgY }}
          className="w-full h-full object-cover object-[center_30%] scale-110"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/55 to-transparent" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-white/40" aria-hidden="true" />
        <div className="absolute inset-0 bg-white/25 md:hidden" aria-hidden="true" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
        <div className="max-w-3xl">
          <motion.img
            src="/logo.png"
            alt="Me 1st Fitness & Coaching logo"
            custom={0}
            variants={fade}
            initial="hidden"
            animate="show"
            className="h-20 sm:h-24 w-auto mb-7 -ml-2"
            data-testid="hero-logo"
          />
          <motion.p
            custom={0}
            variants={fade}
            initial="hidden"
            animate="show"
            className="text-xs tracking-[0.25em] uppercase font-bold text-brand-dark mb-6"
            data-testid="hero-eyebrow"
          >
            Me 1st Fitness &amp; Coaching — Red Deer, Alberta
          </motion.p>

          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-charcoal text-[13vw] sm:text-7xl lg:text-[6.5rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block" data-testid="hero-headline-line-1">
                Me 1st
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block" data-testid="hero-headline-line-2">
                Fitness <span className="text-brand">&amp;</span>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block" data-testid="hero-headline-line-3">
                Coaching
              </motion.span>
            </span>
          </h1>

          <motion.p
            custom={0.7}
            variants={fade}
            initial="hidden"
            animate="show"
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-charcoal/80"
            data-testid="hero-supporting-text"
          >
            Fitness isn't about chasing a smaller body or the latest trend. It's about building
            strength, mobility and resilience for the years ahead. At Me 1st Fitness &amp; Coaching
            we train for longevity — so you can move well, feel capable, stay independent and keep
            doing the things you love. This isn't a quick fix. It's a way of life.
          </motion.p>

          <motion.div
            custom={0.9}
            variants={fade}
            initial="hidden"
            animate="show"
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => navigate("/contact")}
              data-testid="hero-get-started-btn"
              className="group rounded-full bg-brand text-charcoal font-bold px-8 py-4 text-sm tracking-wide flex items-center gap-2 hover:bg-brand-dark hover:text-sand transition-colors duration-300 shadow-[0_8px_30px_rgb(136,206,172,0.5)]"
            >
              GET STARTED
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => navigate("/coaching")}
              data-testid="hero-explore-coaching-btn"
              className="rounded-full border-2 border-charcoal text-charcoal font-bold px-8 py-4 text-sm tracking-wide hover:bg-charcoal hover:text-sand transition-colors duration-300"
            >
              EXPLORE COACHING
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
        className="absolute bottom-24 right-6 sm:right-12 hidden md:block"
        data-testid="hero-rating-badge"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="bg-white/90 backdrop-blur rounded-2xl shadow-[0_20px_50px_rgb(0,0,0,0.15)] border border-black/5 px-6 py-4"
        >
          <p className="font-display font-black text-3xl text-charcoal leading-none">94%</p>
          <p className="text-xs text-charcoal/60 mt-1 font-medium">recommend · 14 reviews</p>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => navigate("/about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-charcoal/50"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold">Scroll</span>
        <span className="w-6 h-10 rounded-full border-2 border-charcoal/30 flex justify-center pt-2">
          <span className="w-1 h-2 rounded-full bg-brand animate-scroll-dot" />
        </span>
      </motion.button>
    </section>
  );
};

export default Hero;
